import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Languages } from "lucide-react";
import { Monogram } from "./Monogram";
import { useScrollTo } from "@/lib/useScrollTo";
import { useLang } from "@/lib/i18n";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const scrollTo = useScrollTo();
  const { copy, lang, toggle } = useLang();

  const go = (target) => {
    setOpen(false);
    setTimeout(() => scrollTo(target), open ? 350 : 0);
  };

  return (
    <header data-testid="site-header" className="fixed top-0 left-0 right-0 z-50 bg-cream/80 backdrop-blur-xl border-b hairline">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <button data-testid="header-logo" onClick={() => go("#top")} className="flex items-center gap-3 text-plum">
          <Monogram className="h-8 md:h-9 w-auto" />
          <span className="font-serif text-sm md:text-base tracking-[0.18em] uppercase">
            House <span className="italic normal-case tracking-normal text-gold-dark">of</span> Alvirah
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-10" data-testid="header-nav">
          {copy.header.nav.map((item) => (
            <button
              key={item.target}
              data-testid={`nav-${item.target.slice(1)}`}
              onClick={() => go(item.target)}
              className="text-[11px] uppercase tracking-[0.3em] text-plum/70 hover:text-plum transition-colors duration-300"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <button
            data-testid="lang-toggle-btn"
            onClick={toggle}
            className="inline-flex items-center gap-2 border border-plum/25 text-plum text-[11px] uppercase tracking-[0.2em] px-3.5 py-2.5 hover:border-gold-dark hover:text-gold-dark transition-colors duration-300"
            aria-label="Switch language"
          >
            <Languages size={14} strokeWidth={1.25} />
            {lang === "en" ? "عربي" : "EN"}
          </button>
          <button
            data-testid="header-book-consultation-btn"
            onClick={() => go("#booking")}
            className="hidden md:inline-block bg-gold text-cream text-[11px] uppercase tracking-[0.25em] px-7 py-3.5 hover:bg-plum hover:text-cream transition-colors duration-300"
          >
            {copy.header.book}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-plum p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} strokeWidth={1.25} /> : <Menu size={22} strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t hairline bg-cream"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {copy.header.nav.map((item) => (
                <button
                  key={item.target}
                  data-testid={`mobile-nav-${item.target.slice(1)}`}
                  onClick={() => go(item.target)}
                  className="text-start font-serif text-2xl text-plum"
                >
                  {item.label}
                </button>
              ))}
              <button
                data-testid="mobile-book-consultation-btn"
                onClick={() => go("#booking")}
                className="mt-2 bg-gold text-cream text-[11px] uppercase tracking-[0.25em] px-7 py-4 text-center"
              >
                {copy.header.book}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
